import { ApckColumn } from '../../main';
import { iso3166FullName } from '../../util/iso3166_tools';
import { COLOR_TO_TYPE } from '../_ui/_utils/useValidationUtils';

const IMPACT_LEVELS = {
    critical: {
        level: '5',
        label: 'Critical',
        icon: 'fa-triangle-exclamation',
        class: 'severity-icon-critical',
        color: 'red',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['red']
    },
    high: {
        level: '4',
        label: 'High',
        icon: 'fa-chevrons-up',
        class: 'severity-icon-high',
        color: 'orange',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['orange']
    },
    medium: {
        level: '3',
        label: 'Medium',
        icon: 'fa-bug',
        class: 'severity-icon-medium',
        color: 'yellow',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['yellow']
    },
    low: {
        level: '2',
        label: 'Low',
        icon: 'fa-chevrons-down',
        class: 'severity-icon-low',
        color: 'green',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['green']
    },
    info: {
        level: '1',
        label: 'Info',
        icon: 'fa-circle-info',
        class: 'severity-icon-info',
        color: 'blue',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['blue']
    },
    investigate: {
        level: '0',
        label: 'Investigate',
        icon: 'fa-binoculars',
        class: 'severity-icon-investigate',
        color: 'light-slate',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['light-slate']
    },
    unknown: {
        label: 'Unknown',
        icon: 'fa-question-circle',
        color: 'light-slate',
        class: 'text-light-slate',
        iconType: COLOR_TO_TYPE['light-slate']
    }
};

const STATUS_MAPPINGS = {
    unfixed: {
        label: 'Unfixed',
        color: 'red'
    },
    fixed: {
        label: 'Fixed',
        color: 'green'
    },
    false_positive: {
        label: 'False Positive',
        color: 'blue'
    },
    acceptable_risk: {
        label: 'Acceptable Risk',
        color: 'yellow'
    },
    unknown: {
        label: 'Unknown',
        color: 'light-slate'
    }
};

// Custom column for displaying risk/vulnerability counts
export const ApckTrustRisksColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        risks: function () {
            return (
                this.rawValue() || {
                    critical: 0,
                    high: 0,
                    medium: 0,
                    low: 0,
                    informational: 0,
                }
            )
        },
    },
    methods: {
        is_active: function (risk) {
            if (risk > 0) {
                return 'active'
            }
        },
    },
    template: `
        <apck-col-wrapper>
            <div class="impact-column">
                <span :class="['impact', 'impact-critical', is_active(risks.critical)]">
                    {{risks.critical}}
                </span>
                <span :class="['impact', 'impact-high', is_active(risks.high)]">
                    {{risks.high}}
                </span>
                <span :class="['impact', 'impact-medium', is_active(risks.medium)]">
                    {{risks.medium}}
                </span>
                <span :class="['impact', 'impact-low', is_active(risks.low)]">
                    {{risks.low}}
                </span>
            </div>
        </apck-col-wrapper>
    `,
});

// Custom column for displaying thirdparty favicons
export const ApckTrustThirdpartyColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        relationships: function () {
            return this.rawValue() || []
        },
    },
    methods: {
        getFavicon(url) {
            return url + '/favicon.ico'
        },
        handleImageError(event) {
            // Replace the img element with a Font Awesome icon
            const icon = document.createElement('i')
            icon.className = 'fa fa-globe trust-icon'
            event.target.parentNode.replaceChild(icon, event.target)
        },
    },
    template: `
        <apck-col-wrapper>
            <div class="favicon-wrapper" v-if="relationships.length">
                <img 
                    class="trust-icon"
                    v-for="(rel, index) in relationships"
                    v-if="index <= 5"
                    :src="getFavicon(rel.thirdparty.origin_url)" 
                    :title="rel.thirdparty.origin_url"
                    @error="handleImageError"
                />
            </div>
        </apck-col-wrapper>
    `,
});

// Custom column for displaying count of relationships
export const ApckTrustCountColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        count: function () {
            const relationships = this.rawValue() || []
            return relationships.length
        },
    },
    template: `
        <apck-col-wrapper>
            <span>{{ count }}</span>
        </apck-col-wrapper>
    `,
});

// Custom column for displaying basic counts
export const ApckTrustBasicCountColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        count: function () {
            const _count = this.rawValue() || 0;
            return _count;
        },
    },
    template: `
        <apck-col-wrapper>
            <span>{{ count }}</span>
        </apck-col-wrapper>
    `,
});

// Custom column for displaying domain list
export const ApckTrustDomainsColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        domains() {
            return this.rawValue() || [];
        },
        displayDomains() {
            // Show first 3 domains, then "+ X more" if there are more
            const domains = this.domains;
            if (domains.length <= 3) {
                return domains.map(d => d.hostname || 'Unknown').join(', ');
            } else {
                const first3 = domains.slice(0, 3).map(d => d.hostname || 'Unknown').join(', ');
                const remaining = domains.length - 3;
                return `${first3} + ${remaining} more`;
            }
        }
    },
    template: `
        <apck-col-wrapper>
            <span :title="domains.map(d => d.hostname).join(', ')">
                {{ displayDomains }}
            </span>
        </apck-col-wrapper>
    `
});

// Custom column for displaying highest risk level
export const ApckTrustHighestRiskColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        riskData() {
            const record = this.record;
            const level = record.highest_risk_level;
            const name = record.highest_risk_name;

            return {
                level: level,
                name: name || 'Unknown'
            };
        },
        riskClass() {
            const level = this.riskData.level;
            if (level === 5) return 'risk-critical';
            if (level === 4) return 'risk-high';
            if (level === 3) return 'risk-medium';
            if (level === 2) return 'risk-low';
            if (level === 1) return 'risk-info';
            return 'risk-none';
        }
    },
    template: `
        <apck-col-wrapper>
            <span :class="['risk-badge', riskClass]">
                {{ riskData.name }}
            </span>
        </apck-col-wrapper>
    `
});

// Custom column for thirdparty with favicon
export const ApckTrustThirdpartyDetailColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        thirdparty() {
            return this.rawValue() || {};
        },
        faviconUrl() {
            if (!this.thirdparty.origin_url) return null;
            return this.thirdparty.origin_url + '/favicon.ico';
        }
    },
    methods: {
        handleImageError(event) {
            // Replace the img element with a Font Awesome icon
            const icon = document.createElement('i')
            icon.className = 'fa fa-globe trust-icon flex dir-column align-center justify-center'
            event.target.parentNode.replaceChild(icon, event.target)
        },
    },
    template: `
        <apck-col-wrapper>
            <div class="thirdparty-detail">
                <img 
                    v-if="faviconUrl"
                    class="trust-icon"
                    :src="faviconUrl" 
                    :title="thirdparty.origin_url"
                    @error="handleImageError"
                />
                <i v-else class="fa fa-globe trust-icon flex dir-column align-center justify-center"></i>
                <span class="thirdparty-hostname">
                    {{ thirdparty.hostname || 'Unknown' }}
                </span>
            </div>
        </apck-col-wrapper>
    `
});

// Custom column for displaying a country code with icon
export const ApckTrustCountryColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        name: function() {
            let _raw = this.rawValue();
            if(!_raw) {
                _raw = 'XX';
            }
            return _raw;
        },
        niceName: function() {
            return iso3166FullName(this.name);
        }
    },
    template: `
        <apck-col-wrapper class="new-ui">
            <ui-layout gap="2" align="center">
                <ui-flag :code="name" size="sm" />
                <span>
                    {{ niceName }}
                </span>
            </ui-layout>
        </apck-col-wrapper>
    `
});

// Custom column for displaying impact level badge (used in vulnerabilities)
export const ApckVulnerabilityImpactColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        impactLevel() {
            const impact = this.rawValue() || 'unknown';
            return impact.toLowerCase();
        },
        impactIconType() {
            const level = this.impactLevel;
            let iconType

            for (const [key, value] of Object.entries(IMPACT_LEVELS)) {
                if (level.includes(key) && !iconType) {
                    iconType = value.iconType
                }
            }

            return iconType
        }
    },
    template: `
        <apck-col-wrapper class="new-ui">
            <ui-icon icon="fa-solid fa-circle" :type="impactIconType" />
        </apck-col-wrapper>
    `
});

// Custom column for displaying vulnerability status
export const ApckVulnerabilityStatusColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        status() {
            return this.rawValue() || 'unknown';
        },
        statusLabel() {
            return STATUS_MAPPINGS[this.status].label
        },
        statusColor() {
            return STATUS_MAPPINGS[this.status].color
        }
    },
    template: `
        <apck-col-wrapper class="new-ui">
            <ui-badge :label="statusLabel" :color="statusColor" fill />
        </apck-col-wrapper>
    `
});

// Custom column for displaying domain information (used in vulnerabilities)
export const ApckVulnerabilityDomainColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        domain() {
            return this.rawValue() || {};
        }
    },
    template: `
        <apck-col-wrapper>
            <div class="domain-detail">
                <span class="domain-hostname">
                    {{ domain.hostname || 'Unknown' }}
                </span>
            </div>
        </apck-col-wrapper>
    `
});

// Custom column for displaying scan run status badge
export const ApckTrustScanStatusColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        status: function () {
            return this.rawValue() || '';
        },
        statusColor: function () {
            var s = this.status;
            if (s === 'completed' || s === 'processed') return 'green';
            if (s === 'failed' || s === 'overdue') return 'red';
            if (s === 'running' || s === 'initializing' || s === 'in_progress') return 'blue';
            if (s === 'aborting' || s === 'pending') return 'orange';
            if (s === 'scheduled' || s === 'projected') return 'light-slate';
            return 'yellow';
        },
    },
    template: `
        <apck-col-wrapper class="new-ui">
            <ui-badge :label="status" :color="statusColor" fill />
        </apck-col-wrapper>
    `,
});

// Custom column for displaying trust level (used in vulnerabilities)
export const ApckVulnerabilityTrustLevelColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        trustLevel() {
            const level = this.rawValue() || {};
            return level;
        },
        trustClass() {
            const level = this.trustLevel.level || 0;
            if (level === 5) return 'trust-critical';
            if (level === 4) return 'trust-high';
            if (level === 3) return 'trust-medium';
            if (level === 2) return 'trust-low';
            if (level === 1) return 'trust-info';
            return 'trust-none';
        }
    },
    template: `
        <apck-col-wrapper>
            <span :class="['trust-badge', trustClass]">
                {{ trustLevel.description || 'None' }}
            </span>
        </apck-col-wrapper>
    `
});
